// src/utils/usageTracker.js
// Lightweight client-side usage instrumentation (best-effort, auth-required on backend).

import api from '../api/client';

const SS_PREFIX = 'dd_usage_session_';

function emitUsageSignal(name, detail) {
  try {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {}
}

function safeGetSessionStorage() {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage || null;
  } catch {
    return null;
  }
}

function makeId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function startUsageSession(moduleId) {
  const ss = safeGetSessionStorage();
  const id = makeId();
  if (ss) ss.setItem(`${SS_PREFIX}${moduleId}`, id);
  return id;
}

export function getUsageSession(moduleId) {
  const ss = safeGetSessionStorage();
  return ss ? (ss.getItem(`${SS_PREFIX}${moduleId}`) || '') : '';
}

export function ensureUsageSession(moduleId) {
  const existing = getUsageSession(moduleId);
  return existing || startUsageSession(moduleId);
}

async function sendEvent(payload) {
  try {
    const result = await api.usage.event(payload);
    if (result?.newly_done) {
      emitUsageSignal('dd-usage-changed', {
        moduleId: payload.module_id,
        sessionId: payload.session_id,
        kind: payload.kind,
      });
    }
    return result;
  } catch (e) {
    if (e?.status === 403 && e?.data?.code === 'usage_limit_reached') {
      emitUsageSignal('dd-usage-limit-reached', {
        moduleId: payload.module_id,
        sessionId: payload.session_id,
        ...(e.data || {}),
      });
    }
    // Ignore: guest users or transient network/auth failures should not break UX.
    return null;
  }
}

function normalizeWorkflowSteps(workflowSteps) {
  if (!Array.isArray(workflowSteps)) return undefined;
  const seen = new Set();
  const out = [];
  for (const raw of workflowSteps) {
    const step = String(raw || '').trim();
    if (!step || seen.has(step)) continue;
    seen.add(step);
    out.push(step);
  }
  return out.length ? out : undefined;
}

export function usageWorkflowFromTabs(tabs, options = {}) {
  const exclude = new Set(options.excludeIds || []);
  const replace = options.replace || {};
  const prefix = options.includeUpload ? ['upload'] : [];
  const ids = Array.isArray(tabs)
    ? tabs.map((tab) => (typeof tab === 'string' ? tab : tab?.id)).filter(Boolean)
    : [];
  const workflow = [...prefix];
  for (const id of ids) {
    if (exclude.has(id)) continue;
    workflow.push(replace[id] || id);
  }
  return normalizeWorkflowSteps(workflow) || [];
}

export function usageStep(moduleId, step, meta = null, workflowSteps = null) {
  const sessionId = ensureUsageSession(moduleId);
  return sendEvent({
    module_id: moduleId,
    session_id: sessionId,
    kind: 'step',
    step,
    meta: meta && typeof meta === 'object' ? meta : undefined,
    workflow_steps: normalizeWorkflowSteps(workflowSteps),
  });
}

export function usageDone(moduleId, reason = 'report_download', units = 1, meta = null, workflowSteps = null) {
  const sessionId = ensureUsageSession(moduleId);
  return sendEvent({
    module_id: moduleId,
    session_id: sessionId,
    kind: 'done',
    reason,
    units,
    meta: meta && typeof meta === 'object' ? meta : undefined,
    workflow_steps: normalizeWorkflowSteps(workflowSteps),
  });
}
